import React, { useState, useEffect } from 'react';
import {
    TextField,
    MenuItem,
    Stack,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

export default function CustomDateTimePicker({ bufferingTime = 0, value, setDateValue, minDateTime = new Date() }) {
    const [date, setDate] = useState(dayjs());
    const [hour, setHour] = useState('12');
    const [minute, setMinute] = useState('00');
    const [period, setPeriod] = useState('AM');
    const [error, setError] = useState('');


    const hours = [...Array(12)].map((_, i) => String(i + 1).padStart(2, '0'));
    const minutes = [...Array(60)].map((_, i) => String(i).padStart(2, '0'));

    // Sync from prop when value changes
    useEffect(() => {
        if (value) {
            const parsed = dayjs(value, 'YYYY-MM-DD hh:mm:ss A');
            if (parsed.isValid()) {
                setDate(parsed);
                let hr = parsed.hour();
                const min = parsed.minute().toString().padStart(2, '0');
                const ampm = hr >= 12 ? 'PM' : 'AM';
                hr = hr % 12 === 0 ? 12 : hr % 12;
                const hrStr = hr.toString().padStart(2, '0');

                setHour(hrStr);
                setMinute(min);
                setPeriod(ampm);
            }
        }
    }, [value]);

    const isBeforeMinTime = (d, h, m, p) => {
        if (!minDateTime || !d) return false;

        const hourInt = parseInt(h, 10);
        const minuteInt = parseInt(m, 10);
        const isPM = p === 'PM';
        const finalHour = isPM && hourInt !== 12 ? hourInt + 12 : (!isPM && hourInt === 12 ? 0 : hourInt);

        const combined = dayjs(d).hour(finalHour).minute(parseInt(m)).second(0);

        return combined.isBefore(dayjs(minDateTime));
    };


    const updateDateTime = (d, h, m, p) => {
        if (!d) return;

        const hourInt = parseInt(h, 10);
        const minuteInt = parseInt(m, 10);
        const isPM = p === 'PM';
        const finalHour = isPM && hourInt !== 12 ? hourInt + 12 : (!isPM && hourInt === 12 ? 0 : hourInt);

        const updated = dayjs(d).hour(finalHour).minute(minuteInt).second(0);

        // If a minDateTime is provided, validate
        if (minDateTime && updated.isBefore(dayjs(minDateTime))) {
            // setError('Pickup time must be in the future.');
            setError(`Pickup time must be at least ${bufferingTime} minutes ahead of the current time.`);
            // Reject update if invalid
            return;
        }

        setDate(updated);
        setDateValue(updated);
    };



    const handleTimeChange = (field, value) => {
        const newHour = field === 'hour' ? value : hour;
        const newMinute = field === 'minute' ? value : minute;
        const newPeriod = field === 'period' ? value : period;

        if (field === 'hour') setHour(value);
        if (field === 'minute') setMinute(value);
        if (field === 'period') setPeriod(value);

        updateDateTime(date, newHour, newMinute, newPeriod);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Stack spacing={2}>
                <DatePicker
                    label="Select Date"
                    value={date}
                    minDate={dayjs(minDateTime)} // restrict earlier dates
                    onChange={(newVal) => {
                        if (newVal) {
                            updateDateTime(newVal, hour, minute, period);
                        }
                    }}
                    renderInput={(params) => (
                        <TextField {...params} fullWidth size="small" />
                    )}
                    inputFormat="DD/MM/YYYY"
                />


                {/* Time Picker Row */}
                <Stack direction="row" spacing={2}>
                    <TextField
                        select
                        label="Hour"
                        value={hour}
                        onChange={(e) => handleTimeChange('hour', e.target.value)}
                        sx={{ minWidth: 100 }}
                        size="small"
                    >
                        {hours.map((h) => {
                            const isDisabled = isBeforeMinTime(date, h, minute, period);
                            return (
                                <MenuItem key={h} value={h} disabled={isDisabled}>
                                    {h}
                                </MenuItem>
                            );
                        })}
                    </TextField>

                    <TextField
                        select
                        label="Minute"
                        value={minute}
                        onChange={(e) => handleTimeChange('minute', e.target.value)}
                        sx={{ minWidth: 100 }}
                        size="small"
                    >
                        {minutes.map((m) => {
                            const isDisabled = isBeforeMinTime(date, hour, m, period);
                            return (
                                <MenuItem key={m} value={m} disabled={isDisabled}>
                                    {m}
                                </MenuItem>
                            );
                        })}
                    </TextField>

                    <TextField
                        select
                        label="AM/PM"
                        value={period}
                        onChange={(e) => handleTimeChange('period', e.target.value)}
                        sx={{ minWidth: 100 }}
                        size="small"
                    >
                        {['AM', 'PM'].map((p) => {
                            const isDisabled = isBeforeMinTime(date, hour, minute, p);
                            return (
                                <MenuItem key={p} value={p} disabled={isDisabled}>
                                    {p}
                                </MenuItem>
                            );
                        })}
                    </TextField>
                </Stack>
                {/* Optional global error text if you want to show below everything */}
                {error && (
                    <span style={{ color: "red", fontSize: "14px" }}>{error}</span>
                )}
            </Stack>
        </LocalizationProvider>
    );
}
